import { DeviceSummary } from '@weenat/client/dist/resources/measurements.type'
import MarkerLegendModal from 'app/src/map/MarkerLegendModal'
import DisplaySettings from 'app/src/myFarm/tools/DisplaySettings'
import MyFarmTools from 'app/src/myFarm/tools/MyFarmTools'
import isNil from 'lodash-es/isNil'
import { memo, useCallback, useEffect, useRef } from 'react'
import Geolocation from '../../../map/Geolocation'
import ControlsContainer from './ControlsContainer'
import DashboardMapLoading from './DashboardMapLoading'
import MapControlsOverlay from './MapControlsOverlay'
import MapOverlay from './MapOverlay'
import Radar from './Radar/Radar'
import useIsRadarEnabled from './Radar/hooks/useIsRadarEnabled'
import RefreshBackgroundMap from './RefreshBackgroundMap'
import ZoomButtons from './ZoomButtons'
import { elementIds } from './constants'
import {
  useBackgroundMapContext,
  useBackgroundMapDispatcher
} from './contexts/BackgroundMapContext'

interface WeatherMapControlsProps {
  summaries: DeviceSummary[]
  filteredSummaries?: DeviceSummary[]
}

const WeatherMapControls: FC<WeatherMapControlsProps> = ({ summaries, filteredSummaries }) => {
  const [isRadarEnabled] = useIsRadarEnabled()

  const {
    api: { map, maps }
  } = useBackgroundMapContext()
  const dispatch = useBackgroundMapDispatcher()

  const autocompleteInputRef = useRef(null)

  const onGeolocationSuccess = useCallback<PositionCallback>(
    ({ coords: { latitude, longitude } }) => {
      dispatch({
        type: 'setGeolocation',
        newGeolocation: {
          lat: latitude,
          lng: longitude
        }
      })
    },
    [dispatch]
  )

  useEffect(() => {
    if (!isNil(map) && !isNil(maps) && !isNil(autocompleteInputRef.current)) {
      new maps.places.PlacesService(map)

      const googlePlacesAutocompleteInput = new maps.places.Autocomplete(
        autocompleteInputRef.current
      )

      const placeChangeListener = maps.event.addListener(
        googlePlacesAutocompleteInput,
        'place_changed',
        () => {
          const { geometry: { location, viewport } = {} } = googlePlacesAutocompleteInput.getPlace()

          if (!isNil(location) && !isNil(viewport)) {
            dispatch({
              type: 'setTownGeolocation',
              newGeolocation: {
                location: {
                  lat: location.lat(),
                  lng: location.lng()
                },
                bounds: viewport
              }
            })
          }
        }
      )

      return () => maps.event.removeListener(placeChangeListener)
    }
    return undefined
  }, [map])

  return (
    <MapControlsOverlay>
      {!isRadarEnabled ? (
        <Flex $flex={1} $flexDirection='column' $justifyContent='space-between'>
          {/* TOP */}
          <MyFarmTools
            summaries={summaries}
            filteredResults={filteredSummaries}
            displaySettingsElement={<DisplaySettings isWeatherMap />}
          />

          {/* FILL SPACE */}
          <Flex $flexDirection='column' $flex={1} $width='100%' $gap='lg'>
            <Flex
              $flexDirection='column'
              $gap='lg'
              $justifyContent='center'
              $alignItems='center'
              $alignSelf='flex-end'
              $flex={1}
              $mt={80}
            >
              <Flex $flexDirection='column' $gap='md'>
                <ControlsContainer $flexDirection='column-reverse' $py='sm' $gap='sm'>
                  <ZoomButtons />
                </ControlsContainer>

                <ControlsContainer>
                  <Geolocation onSuccess={onGeolocationSuccess} tooltipPlacement='left' />
                </ControlsContainer>

                <ControlsContainer>
                  <RefreshBackgroundMap tooltipPlacement='left' $size='xl' $m='sm' />
                </ControlsContainer>

                <Radar map={map} maps={maps} />

                <ControlsContainer>
                  <MarkerLegendModal tooltipPlacement='left' />
                </ControlsContainer>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Radar map={map} maps={maps} />
      )}
    </MapControlsOverlay>
  )
}

type WeatherMapOverlayProps = {
  isLoading: boolean
  summaries: DeviceSummary[]
  filteredSummaries?: DeviceSummary[]
}

const WeatherMapOverlay: FC<WeatherMapOverlayProps> = ({
  children,
  isLoading,
  summaries,
  filteredSummaries
}) => {
  const {
    api: { map, maps }
  } = useBackgroundMapContext()

  const displayControls = !isNil(map) && !isNil(maps)

  return (
    <MapOverlay id={elementIds.OverlayContainer} $flexDirection='column'>
      {isLoading ? (
        <DashboardMapLoading />
      ) : (
        <>
          {displayControls ? (
            <WeatherMapControls
              // all summaries not only current
              summaries={summaries}
              filteredSummaries={filteredSummaries}
            />
          ) : null}
          {children}
        </>
      )}
    </MapOverlay>
  )
}

export default memo(WeatherMapOverlay)
